import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Science as ScienceIcon
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';

const TabPanel = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
  </div>
);

const KFLChemistry = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [filters, setFilters] = useState({
    heat_no: '',
    test_date_from: '',
    test_date_to: ''
  });

  const [formData, setFormData] = useState({
    heat_no: '',
    test_date: new Date().toISOString().split('T')[0],
    test_type: 'chemical',
    carbon: '',
    manganese: '',
    phosphorus: '',
    sulphur: '',
    silicon: '',
    chromium: '',
    nickel: '',
    molybdenum: '',
    aluminum: '',
    vanadium: '',
    titanium: '',
    copper: '',
    nitrogen: '',
    oxygen: '',
    test_method: '',
    equipment_used: '',
    tested_by: '',
    verified_by: '',
    remarks: ''
  });

  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { api } = useAuth();

  // Fetch KFL Chemistry data
  const { data: chemistryData, isLoading, error, refetch } = useQuery(
    ['kflChemistry', page, rowsPerPage, searchTerm, filters],
    async () => {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...filters
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await api.get(`/raw-material/chemistry?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true,
      onError: (error) => {
        enqueueSnackbar(`Error fetching chemistry data: ${error.message}`, { variant: 'error' });
      }
    }
  );

  // Create mutation
  const createMutation = useMutation(
    (data) => api.post('/raw-material/chemistry', data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kflChemistry');
        enqueueSnackbar('Chemistry analysis record created successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error creating record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }) => api.put(`/raw-material/chemistry/${id}`, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kflChemistry');
        enqueueSnackbar('Chemistry analysis record updated successfully', { variant: 'success' });
        handleCloseDialog();
      },
      onError: (error) => {
        enqueueSnackbar(`Error updating record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    (id) => api.delete(`/raw-material/chemistry/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('kflChemistry');
        enqueueSnackbar('Chemistry analysis record deleted successfully', { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(`Error deleting record: ${error.response?.data?.message || error.message}`, { variant: 'error' });
      }
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const submitData = {
      ...formData,
      carbon: formData.carbon ? parseFloat(formData.carbon) : null,
      manganese: formData.manganese ? parseFloat(formData.manganese) : null,
      phosphorus: formData.phosphorus ? parseFloat(formData.phosphorus) : null,
      sulphur: formData.sulphur ? parseFloat(formData.sulphur) : null,
      silicon: formData.silicon ? parseFloat(formData.silicon) : null,
      chromium: formData.chromium ? parseFloat(formData.chromium) : null,
      nickel: formData.nickel ? parseFloat(formData.nickel) : null,
      molybdenum: formData.molybdenum ? parseFloat(formData.molybdenum) : null,
      aluminum: formData.aluminum ? parseFloat(formData.aluminum) : null,
      vanadium: formData.vanadium ? parseFloat(formData.vanadium) : null,
      titanium: formData.titanium ? parseFloat(formData.titanium) : null,
      copper: formData.copper ? parseFloat(formData.copper) : null,
      nitrogen: formData.nitrogen ? parseFloat(formData.nitrogen) : null,
      oxygen: formData.oxygen ? parseFloat(formData.oxygen) : null
    };

    if (editingItem) {
      updateMutation.mutate({ id: editingItem.id, data: submitData });
    } else {
      createMutation.mutate(submitData);
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      heat_no: item.heat_no || '',
      test_date: item.test_date || '',
      test_type: item.test_type || 'chemical',
      carbon: item.carbon || '',
      manganese: item.manganese || '',
      phosphorus: item.phosphorus || '',
      sulphur: item.sulphur || '',
      silicon: item.silicon || '',
      chromium: item.chromium || '',
      nickel: item.nickel || '',
      molybdenum: item.molybdenum || '',
      aluminum: item.aluminum || '',
      vanadium: item.vanadium || '',
      titanium: item.titanium || '',
      copper: item.copper || '',
      nitrogen: item.nitrogen || '',
      oxygen: item.oxygen || '',
      test_method: item.test_method || '',
      equipment_used: item.equipment_used || '',
      tested_by: item.tested_by || '',
      verified_by: item.verified_by || '',
      remarks: item.remarks || ''
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingItem(null);
    setFormData({
      heat_no: '',
      test_date: new Date().toISOString().split('T')[0],
      test_type: 'chemical',
      carbon: '',
      manganese: '',
      phosphorus: '',
      sulphur: '',
      silicon: '',
      chromium: '',
      nickel: '',
      molybdenum: '',
      aluminum: '',
      vanadium: '',
      titanium: '',
      copper: '',
      nitrogen: '',
      oxygen: '',
      test_method: '',
      equipment_used: '',
      tested_by: '',
      verified_by: '',
      remarks: ''
    });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this chemistry analysis record?')) {
      deleteMutation.mutate(id);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString();
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          Error loading chemistry data: {error.message}
        </Alert>
      </Box>
    );
  }

  const chemistryAnalysis = chemistryData?.data?.chemistry || [];
  const pagination = chemistryData?.data?.pagination || {};
  const totalCount = pagination.total || 0;

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        KFL Chemistry Analysis
      </Typography>

      {/* Filter Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by heat no..."
              InputProps={{
                endAdornment: <SearchIcon />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Heat No"
              value={filters.heat_no}
              onChange={(e) => setFilters({ ...filters, heat_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              InputLabelProps={{ shrink: true }}
              value={filters.test_date_from}
              onChange={(e) => setFilters({ ...filters, test_date_from: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              InputLabelProps={{ shrink: true }}
              value={filters.test_date_to}
              onChange={(e) => setFilters({ ...filters, test_date_to: e.target.value })}
            />
          </Grid>
          <Grid item>
            <Button
              variant="outlined"
              onClick={() => refetch()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Box mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Chemistry Analysis
        </Button>
      </Box>

      {/* Data Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test Date</TableCell>
              <TableCell>Heat No</TableCell>
              <TableCell>C (%)</TableCell>
              <TableCell>Mn (%)</TableCell>
              <TableCell>P (%)</TableCell>
              <TableCell>S (%)</TableCell>
              <TableCell>Si (%)</TableCell>
              <TableCell>Cr (%)</TableCell>
              <TableCell>Ni (%)</TableCell>
              <TableCell>Mo (%)</TableCell>
              <TableCell>Tested By</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : chemistryAnalysis.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  No chemistry analysis records found
                </TableCell>
              </TableRow>
            ) : (
              chemistryAnalysis.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{formatDate(item.test_date)}</TableCell>
                  <TableCell>{item.heat_no}</TableCell>
                  <TableCell>{item.carbon}</TableCell>
                  <TableCell>{item.manganese}</TableCell>
                  <TableCell>{item.phosphorus}</TableCell>
                  <TableCell>{item.sulphur}</TableCell>
                  <TableCell>{item.silicon}</TableCell>
                  <TableCell>{item.chromium}</TableCell>
                  <TableCell>{item.nickel}</TableCell>
                  <TableCell>{item.molybdenum}</TableCell>
                  <TableCell>{item.tested_by}</TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          showFirstButton
          showLastButton
        />
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <form onSubmit={handleSubmit}>
          <DialogTitle>
            {editingItem ? 'Edit Chemistry Analysis' : 'Add New Chemistry Analysis'}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ width: '100%' }}>
              <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                <Tab label="Basic Info" />
                <Tab label="Chemical Composition" />
                <Tab label="Additional Elements" />
                <Tab label="Test Details" />
              </Tabs>

              <TabPanel value={activeTab} index={0}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Heat Number"
                      value={formData.heat_no}
                      onChange={(e) => setFormData({ ...formData, heat_no: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Test Date"
                      InputLabelProps={{ shrink: true }}
                      value={formData.test_date}
                      onChange={(e) => setFormData({ ...formData, test_date: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Test Method"
                      value={formData.test_method}
                      onChange={(e) => setFormData({ ...formData, test_method: e.target.value })}
                      placeholder="e.g., Optical Emission Spectroscopy"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Equipment Used"
                      value={formData.equipment_used}
                      onChange={(e) => setFormData({ ...formData, equipment_used: e.target.value })}
                      placeholder="e.g., Spectro MAXx"
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={activeTab} index={1}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Carbon (%)"
                      value={formData.carbon}
                      onChange={(e) => setFormData({ ...formData, carbon: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Manganese (%)"
                      value={formData.manganese}
                      onChange={(e) => setFormData({ ...formData, manganese: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Phosphorus (%)"
                      value={formData.phosphorus}
                      onChange={(e) => setFormData({ ...formData, phosphorus: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Sulphur (%)"
                      value={formData.sulphur}
                      onChange={(e) => setFormData({ ...formData, sulphur: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Silicon (%)"
                      value={formData.silicon}
                      onChange={(e) => setFormData({ ...formData, silicon: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Chromium (%)"
                      value={formData.chromium}
                      onChange={(e) => setFormData({ ...formData, chromium: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={activeTab} index={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Nickel (%)"
                      value={formData.nickel}
                      onChange={(e) => setFormData({ ...formData, nickel: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Molybdenum (%)"
                      value={formData.molybdenum}
                      onChange={(e) => setFormData({ ...formData, molybdenum: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Aluminum (%)"
                      value={formData.aluminum}
                      onChange={(e) => setFormData({ ...formData, aluminum: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Vanadium (%)"
                      value={formData.vanadium}
                      onChange={(e) => setFormData({ ...formData, vanadium: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Titanium (%)"
                      value={formData.titanium}
                      onChange={(e) => setFormData({ ...formData, titanium: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Copper (%)"
                      value={formData.copper}
                      onChange={(e) => setFormData({ ...formData, copper: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Nitrogen (%)"
                      value={formData.nitrogen}
                      onChange={(e) => setFormData({ ...formData, nitrogen: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      type="number"
                      step="0.001"
                      label="Oxygen (%)"
                      value={formData.oxygen}
                      onChange={(e) => setFormData({ ...formData, oxygen: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </TabPanel>

              <TabPanel value={activeTab} index={3}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Tested By"
                      value={formData.tested_by}
                      onChange={(e) => setFormData({ ...formData, tested_by: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Verified By"
                      value={formData.verified_by}
                      onChange={(e) => setFormData({ ...formData, verified_by: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Remarks"
                      value={formData.remarks}
                      onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                    />
                  </Grid>
                </Grid>
              </TabPanel>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button 
              type="submit" 
              variant="contained"
              disabled={createMutation.isLoading || updateMutation.isLoading}
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default KFLChemistry;